// Session restore at app start (2026-10-17).
//
// Before: the whole app waited for GET /api/auth/me before rendering anything, and ANY failure of
// that request (a timeout while the single Cloud Run instance was busy, or Cloud Run's own 429
// "Rate exceeded.") deleted the stored token - the customer was thrown to the sign-in screen.
// Now: with a stored token and a cached basic profile the app renders immediately and /me is
// checked in the background; only a real authorization rejection (401/403) signs out; transient
// failures are retried a bounded number of times. The server still verifies the token on every
// API request, so this changes when the UI appears, not who is allowed in.

const CACHE_KEY = 'cv.session.tenant';
// Basic, non-secret profile fields only - nothing that grants access by itself.
const CACHED_FIELDS = ['id', 'email', 'firstName', 'name', 'displayName', 'plan', 'trialEndsAt', 'subscriptionActive', 'emailVerifiedAt'];

function store(storage) {
  const target = storage === undefined ? (typeof window !== 'undefined' ? window.localStorage : null) : storage;
  return {
    get(key) { try { return target ? target.getItem(key) : null; } catch { return null; } },
    set(key, value) { try { if (target) target.setItem(key, value); } catch { /* storage unavailable */ } },
    remove(key) { try { if (target) target.removeItem(key); } catch { /* storage unavailable */ } }
  };
}

export function cacheTenant(tenant, storage) {
  if (!tenant?.id) return;
  const basic = {};
  for (const field of CACHED_FIELDS) if (tenant[field] !== undefined) basic[field] = tenant[field];
  store(storage).set(CACHE_KEY, JSON.stringify(basic));
}

export function readCachedTenant(storage) {
  try {
    const parsed = JSON.parse(store(storage).get(CACHE_KEY) || 'null');
    return parsed && typeof parsed.id === 'string' && typeof parsed.email === 'string' ? parsed : null;
  } catch {
    return null;
  }
}

export function clearCachedTenant(storage) {
  store(storage).remove(CACHE_KEY);
}

/** Only an explicit authorization rejection means the session is no longer valid. */
export function isAuthRejection(error) {
  const status = Number(error?.response?.status || 0);
  return status === 401 || status === 403;
}

/**
 * Verifies the stored session. Resolves to:
 *   { status: 'valid', tenant }    - /me succeeded
 *   { status: 'rejected' }         - 401/403: the token is no longer valid
 *   { status: 'unreachable' }      - still failing after the bounded retries (keep the token)
 */
export async function verifySession({ me, attempts = 3, delaysMs = [1500, 4000], sleep = ms => new Promise(resolve => setTimeout(resolve, ms)) }) {
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      const res = await me();
      if (res?.tenant?.id) return { status: 'valid', tenant: res.tenant };
      return { status: 'rejected' };
    } catch (error) {
      if (isAuthRejection(error)) return { status: 'rejected' };
      if (attempt < attempts) await sleep(delaysMs[Math.min(attempt - 1, delaysMs.length - 1)]);
    }
  }
  return { status: 'unreachable' };
}
